import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, AlertCircle, CheckCircle2, Info, Plug, X, ArrowRight } from 'lucide-react';
import { warningsApi } from '../api/warnings.js';
import useAppVersion from '../hooks/useAppVersion.js';
import { useTenant } from './TenantContext.jsx';

const POLL_MS = 30_000;
const KIND_LABEL_KEYS = {
    conf_apply_failure: 'warnings.kind.confApplyFailure',
    watchdog_fire: 'warnings.kind.watchdogFire',
    ami_disconnect: 'warnings.kind.amiDisconnect',
    ami_error: 'warnings.kind.amiError',
    ami_connect: 'warnings.kind.amiConnect',
};

export default function SystemWarnings() {
    const { t } = useTranslation('system');
    const { tenants, hostId, isSuperAdmin } = useTenant();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const ver = useAppVersion();   // popover 頁尾的 版號·sha；頂欄的版號由 VersionBadge 獨立顯示
    const popRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        async function poll() {
            try {
                const r = await warningsApi.fetch();
                if (!cancelled) {
                    setData(r);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) setError(e);
            }
        }
        poll();
        const t = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    // 點擊外部關閉
    useEffect(() => {
        if (!open) return;
        function onDoc(ev) {
            if (popRef.current && !popRef.current.contains(ev.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    // 縮到「目前切換器選的 tenant」——看 osense 就只看 osense，不再把帳號名下其他 tenant
    // （甚至別區的 rabbit）的警告一起丟出來。sidecar 回的是該台全租戶視野，這裡才過濾。
    // 沒有選定 tenant（超管總覽 / 尚未載入）→ 退回「帳號可見的全部」。
    const scopeHostIds = hostId
        ? new Set([hostId])
        : new Set(tenants.map((t) => t.hostId));
    const filteredTenants = (data?.tenants || []).filter((t) => scopeHostIds.has(t.hostId));
    // 0805 東訊回報「系統狀態固定顯示 AMI 錯誤」的三個成因,一起收在這裡：
    //   ① 事件環是記憶體 ring buffer,沒有時間過濾 → sidecar 開機那一瞬的 ECONNREFUSED
    //      會一直掛著（實查台灣 prod：AMI 其實 connected=true,錯誤是 10 秒前的開機重試）
    //   ② 那筆事件沒有 hostId → 原本的過濾條件 `!f.hostId` 直接放行給每個租戶
    //   ③ AMI 是我們跟自家 Asterisk 的內部管理連線,對客戶沒有任何意義
    //   → 24 小時前的事件不再顯示；沒有 hostId 的機房層事件只給超管。
    const FAILURE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
    const failureCutoff = Date.now() - FAILURE_MAX_AGE_MS;
    const filteredFailures = (data?.recentFailures || []).filter((f) => {
        const at = typeof f.at === 'number' ? f.at : Date.parse(f.at);   // at 是 ISO 字串
        if (Number.isFinite(at) && at < failureCutoff) return false;
        if (!f.hostId) return isSuperAdmin;          // 機房層 → 只給超管
        return scopeHostIds.has(f.hostId);
    });

    const summary = computeSummary(data, filteredTenants, filteredFailures, t, isSuperAdmin);

    if (error && !data) {
        return (
            <button
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cream-100 border border-cream-200 text-ink-400 text-xs"
                title={t('warnings.fetchFailed', { message: error.message })}
            >
                <Plug size={12} /> {t('warnings.statusUnknown')}
            </button>
        );
    }

    if (!data) return null;

    return (
        <div className="relative" ref={popRef}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={[
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition',
                    summary.tone === 'error' ? 'bg-danger-100/60 text-danger-500 border border-danger-500/30' :
                    summary.tone === 'warn' ? 'bg-warn-100 text-warn-500 border border-warn-500/30' :
                    'bg-success-100/60 text-success-500 border border-success-500/30',
                ].join(' ')}
                title={summary.title}
            >
                <SeverityIcon tone={summary.tone} />
                <span>{summary.label}</span>
            </button>

            {open && (
                <div className="absolute top-full right-0 mt-2 w-[420px] max-h-[70vh] overflow-y-auto bg-white border border-cream-200 rounded-lg shadow-xl z-30">
                    <div className="px-4 py-3 border-b border-cream-100 flex items-center justify-between">
                        <div className="font-medium text-sm text-ink-900">{t('warnings.popoverTitle')}</div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-ink-400">
                                {new Date(data.generatedAt).toLocaleTimeString()}
                            </span>
                            <button onClick={() => setOpen(false)} className="text-ink-400 hover:text-ink-700">
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* AMI＝我們跟自家 Asterisk 的內部管理連線,客戶看了只會焦慮 → 只給超管 */}
                        {isSuperAdmin && <AmiBlock ami={data.ami} />}
                        <TenantsBlock tenants={filteredTenants} />
                        <FailuresBlock failures={filteredFailures} counters={data.counters} />
                    </div>
                    <div className="px-4 py-2.5 border-t border-cream-100 flex items-center justify-between gap-2">
                        <Link to="/system" onClick={() => setOpen(false)}
                            className="inline-flex items-center gap-1 text-xs text-accent-700 hover:underline">
                            {t('warnings.viewFull')} <ArrowRight size={12} />
                        </Link>
                        {ver?.version && (
                            <span className="text-[10px] font-mono text-ink-400 truncate" title={ver.deployedAt ? new Date(ver.deployedAt).toLocaleString() : ''}>
                                v{ver.version}{ver.sha ? ` · ${ver.sha}` : ''}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function computeSummary(data, tenants, failures, t, isSuperAdmin = false) {
    if (!data) return { tone: 'success', label: '...', title: '' };

    // AMI 斷線是機房內部狀態 —— 客戶帳號不該因此看到紅色警示（他也無從處理）。
    const amiDown = isSuperAdmin && !data.ami?.connected;
    let regIssueCount = 0;
    let qualifyIssueCount = 0;
    for (const t of tenants) {
        regIssueCount += t.registrations?.issues?.length || 0;
        qualifyIssueCount += t.qualify?.unreachable?.length || 0;
    }
    // 最近 5 分鐘內的 error / warn
    const cutoff = Date.now() - 5 * 60_000;
    const recentErrors = failures.filter(
        (f) => f.severity === 'error' && new Date(f.at).getTime() >= cutoff
    ).length;
    const recentWarns = failures.filter(
        (f) => f.severity === 'warn' && new Date(f.at).getTime() >= cutoff
    ).length;

    const errorTotal = (amiDown ? 1 : 0) + regIssueCount + qualifyIssueCount + recentErrors;
    const warnTotal = recentWarns;

    if (errorTotal > 0) {
        return {
            tone: 'error',
            label: t('warnings.summary.problems', { n: errorTotal }),
            title: amiDown
                ? t('warnings.amiDisconnected')
                : t('warnings.summary.errorTitle', { reg: regIssueCount, qualify: qualifyIssueCount, errors: recentErrors }),
        };
    }
    if (warnTotal > 0) {
        return { tone: 'warn', label: t('warnings.summary.reminders', { n: warnTotal }), title: t('warnings.summary.warnTitle', { n: warnTotal }) };
    }
    return { tone: 'success', label: t('warnings.summary.ok'), title: t('warnings.summary.okTitle') };
}

function SeverityIcon({ tone }) {
    if (tone === 'error') return <AlertCircle size={12} />;
    if (tone === 'warn') return <AlertTriangle size={12} />;
    return <CheckCircle2 size={12} />;
}

function AmiBlock({ ami }) {
    const { t } = useTranslation('system');
    const ok = ami?.connected;
    return (
        <div>
            <SectionLabel>{t('warnings.asteriskConnection')}</SectionLabel>
            <div className="flex items-center gap-2 text-sm">
                <span
                    className={[
                        'size-2 rounded-full',
                        ok ? 'bg-success-500' : 'bg-danger-500',
                    ].join(' ')}
                />
                <span className={ok ? 'text-ink-700' : 'text-danger-500 font-medium'}>
                    {ok ? t('warnings.amiConnected') : t('warnings.amiDown')}
                </span>
                {ok && ami.connectedSince && (
                    <span className="text-[10px] font-mono text-ink-400">
                        {t('warnings.since', { time: new Date(ami.connectedSince).toLocaleTimeString() })}
                    </span>
                )}
                {!ok && ami?.lastDisconnectAt && (
                    <span className="text-[10px] font-mono text-ink-400">
                        {t('warnings.disconnectedAt', { time: new Date(ami.lastDisconnectAt).toLocaleTimeString() })}
                    </span>
                )}
            </div>
        </div>
    );
}

function TenantsBlock({ tenants }) {
    const { t } = useTranslation('system');
    if (tenants.length === 0) {
        return (
            <div>
                <SectionLabel>{t('warnings.tenantPbx')}</SectionLabel>
                <div className="text-xs text-ink-400 italic">{t('warnings.noTenantData')}</div>
            </div>
        );
    }
    return (
        <div>
            <SectionLabel>{t('warnings.tenantPbx')}</SectionLabel>
            <div className="space-y-1.5">
                {tenants.map((t) => (
                    <TenantRow key={t.hostId} tenant={t} />
                ))}
            </div>
        </div>
    );
}

function TenantRow({ tenant }) {
    const { t } = useTranslation('system');
    const reg = tenant.registrations || { total: 0, registered: 0, issues: [] };
    const qa = tenant.qualify || { total: 0, reachable: 0, unreachable: [] };
    const regOk = reg.total > 0 && reg.registered === reg.total;
    const qaOk = qa.total === 0 || qa.unreachable.length === 0;
    const allOk = regOk && qaOk;

    return (
        <div className="bg-cream-50 border border-cream-200 rounded-md px-3 py-2">
            <div className="flex items-center justify-between mb-1">
                <code className="font-mono text-xs text-ink-900 font-medium">{tenant.hostId}</code>
                <span
                    className={[
                        'size-1.5 rounded-full',
                        allOk ? 'bg-success-500' : 'bg-danger-500',
                    ].join(' ')}
                />
            </div>
            <div className="text-[11px] text-ink-500 space-y-0.5">
                <div>
                    {t('warnings.registered')} <span className={regOk ? 'text-success-500' : 'text-danger-500'}>
                        {reg.registered}/{reg.total}
                    </span>
                    {reg.issues.length > 0 && (
                        <span className="text-danger-500 ml-2">
                            {t('warnings.badExt', { exts: reg.issues.map((i) => `${i.ext}(${i.status})`).join('、') })}
                        </span>
                    )}
                </div>
                {qa.total > 0 && (
                    <div>
                        {t('warnings.pbxReachable')} <span className={qaOk ? 'text-success-500' : 'text-danger-500'}>
                            {qa.reachable}/{qa.total}
                        </span>
                        {qa.unreachable.length > 0 && (
                            <span className="text-danger-500 ml-2">
                                {qa.unreachable.map((u) => `${u.ext}(${u.status})`).join('、')}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function FailuresBlock({ failures, counters }) {
    const { t } = useTranslation('system');
    if (failures.length === 0) {
        return (
            <div>
                <SectionLabel>{t('warnings.recentEvents')}</SectionLabel>
                <div className="text-xs text-ink-400 italic">{t('warnings.noEvents')}</div>
            </div>
        );
    }
    // 事件多時不佔版面：區塊限高、內部捲動（原本硬砍前 8 筆，事件一多就看不到歷史）
    const top = failures.slice(0, 50);
    return (
        <div>
            <SectionLabel>
                {t('warnings.recentEvents')}
                {counters && (
                    <span className="ml-2 text-[10px] font-mono text-ink-400">
                        {t('warnings.counters', { apply: counters.confApplyFailure, watchdog: counters.watchdogFire, ami: counters.amiDisconnect })}
                    </span>
                )}
            </SectionLabel>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1 overscroll-contain">
                {top.map((f, i) => (
                    <FailureRow key={i} failure={f} />
                ))}
            </div>
        </div>
    );
}

function FailureRow({ failure }) {
    const { t } = useTranslation('system');
    const tone = failure.severity === 'error' ? 'danger' : failure.severity === 'warn' ? 'warn' : 'ink';
    return (
        <div className="bg-white border border-cream-200 rounded px-2.5 py-1.5">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[11px]">
                    {failure.severity === 'error' ? (
                        <AlertCircle size={11} className="text-danger-500" />
                    ) : failure.severity === 'warn' ? (
                        <AlertTriangle size={11} className="text-warn-500" />
                    ) : (
                        <Info size={11} className="text-ink-400" />
                    )}
                    <span className="font-medium text-ink-700">
                        {KIND_LABEL_KEYS[failure.kind] ? t(KIND_LABEL_KEYS[failure.kind]) : failure.kind}
                    </span>
                    {failure.hostId && (
                        <code className="font-mono text-[10px] text-ink-400">[{failure.hostId}]</code>
                    )}
                </span>
                <span className="font-mono text-[10px] text-ink-400">
                    {new Date(failure.at).toLocaleTimeString()}
                </span>
            </div>
            <div className={[
                'text-[11px] mt-0.5',
                tone === 'danger' ? 'text-danger-500' : tone === 'warn' ? 'text-warn-500' : 'text-ink-500',
            ].join(' ')}>
                {failure.message}
            </div>
        </div>
    );
}

function SectionLabel({ children }) {
    return (
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
            {children}
        </div>
    );
}
